// src/api/enrollmentApi.js
import axios from "axios";

const API_URL = "/api/enrollments";

/**
 * Enrollment API - Primary workflow for student enrollment
 *
 * New endpoints:
 * - getEnrollmentById(id) - Get single enrollment
 * - getStudentEnrollmentHistory(studentId) - Full enrollment history
 * - getStudentCurrentEnrollment(studentId) - Current active enrollment
 * - addEnrollment supports inline student creation via `student` object
 */

/* 🔹 Get all enrollments with optional filters */
export const getEnrollments = async (filters = {}) => {
  try {
    const res = await axios.get(API_URL, { params: filters });
    return res.data.data || [];
  } catch (err) {
    console.error("❌ Error fetching enrollments:", err);
    return [];
  }
};

/* 🔹 Get single enrollment by ID */
export const getEnrollmentById = async (id) => {
  try {
    const res = await axios.get(`${API_URL}/${id}`);
    return res.data.data || null;
  } catch (err) {
    console.error("❌ Error fetching enrollment:", err);
    return null;
  }
};

/* 🔹 Get enrollment history for a student */
export const getStudentEnrollmentHistory = async (studentId) => {
  try {
    const res = await axios.get(`${API_URL}/student/${studentId}/history`);
    return res.data.data || { student: null, enrollment_history: [], total_enrollments: 0 };
  } catch (err) {
    console.error("❌ Error fetching enrollment history:", err);
    console.error("Error details:", err.response?.data);
    return { student: null, enrollment_history: [], total_enrollments: 0 };
  }
};

/* 🔹 Get current active enrollment for a student */
export const getStudentCurrentEnrollment = async (studentId) => {
  try {
    const res = await axios.get(`${API_URL}/student/${studentId}/current`);
    return res.data.data || null;
  } catch (err) {
    // 404 means no active enrollment - not an error
    if (err.response?.status === 404) {
      return null;
    }
    console.error("❌ Error fetching current enrollment:", err);
    return null;
  }
};

/* 🔹 Legacy: Get enrollments by student ID (uses new history endpoint) */
export const getEnrollmentsByStudent = async (studentId) => {
  try {
    const history = await getStudentEnrollmentHistory(studentId);
    return history.enrollment_history || [];
  } catch (err) {
    console.error("❌ Error fetching enrollments by student:", err);
    return [];
  }
};

/**
 * 🔹 Add new enrollment (supports inline student creation)
 *
 * For existing student:
 * { student_id, session_id, grade_id, subject_ids[], status?, enrolled_at?, join_waitlist? }
 *
 * For new student (inline creation):
 * { student: { full_name, gender, birth_date?, parent_id? }, session_id, grade_id, subject_ids[], ... }
 *
 * Response includes: { success, message, data, student_created, is_waitlisted, waitlist_position }
 */
export const addEnrollment = async (data) => {
  try {
    const res = await axios.post(API_URL, data);
    return res.data;
  } catch (err) {
    console.error("❌ Error adding enrollment:", err);
    throw err.response?.data || err;
  }
};

/* 🔹 Edit existing enrollment */
export const editEnrollment = async (id, data) => {
  try {
    const res = await axios.put(`${API_URL}/${id}`, data);
    return res.data;
  } catch (err) {
    console.error("❌ Error editing enrollment:", err);
    throw err.response?.data || err;
  }
};

/* 🔹 Delete enrollment */
export const deleteEnrollment = async (id) => {
  try {
    const res = await axios.delete(`${API_URL}/${id}`);
    return res.data;
  } catch (err) {
    console.error("❌ Error deleting enrollment:", err);
    throw err.response?.data || err;
  }
};

/* 🔹 Get subjects by grade */
export const getSubjectsByGrade = async (gradeId) => {
  try {
    const res = await axios.get(`${API_URL}/curriculumSubjects/by-grade`, {
      params: { gradeId },
    });
    console.log("✅ Fetched subjects by grade:", res.data);

    // ✅ Normalize for frontend: include id, name, and type (mandatory/elective)
    const data = res.data?.data || [];
    return data.map((s) => ({
      id: s.id || s.subject_id,
      name: s.name || s.subject_name || s.Subject?.name || "Unnamed Subject",
      is_mandatory:
        !!s.is_mandatory || (s.type && s.type.toLowerCase() === "mandatory"),
      type: s.type || s.Subject?.type || "elective", // fallback
    }));
  } catch (err) {
    console.error("❌ Error fetching subjects by grade:", err);
    return [];
  }
};

/* 🔹 Get students by grade(s) */
export const getStudentsByGrades = async (gradeIds) => {
  try {
    const params = {};
    if (gradeIds) params.gradeIds = gradeIds;

    const res = await axios.get(`${API_URL}/students/by-grades`, { params });
    return Array.isArray(res.data) ? res.data : res.data?.data || [];
  } catch (err) {
    console.error("❌ Error fetching students by grade:", err);
    return [];
  }
};

/* 🔹 Class-level promotion */
export const getPromotionCandidates = async (sessionId, gradeId) => {
  const res = await axios.get(`${API_URL}/promotion/candidates`, {
    params: { session_id: sessionId, grade_id: gradeId },
  });
  return res.data?.data || { from_grade: null, next_grade: null, students: [] };
};

export const executePromotion = async (payload) => {
  const res = await axios.post(`${API_URL}/promotion/execute`, payload);
  return res.data;
};